import { Modal } from "antd";
import { useLoading } from "components/GlobalLoading";
import NewTable from "components/NewTable";
import { SimulacaoArrecadacaoItem } from "core/features/arrecadacoes/typings";
import useSimulacaoArrecadacao from "core/features/arrecadacoes/useSimulacaoArrecadacao";
import { useState } from "react";
import FooterModal from "ui/components/Button/FooterModal";
import useTableSimulacaoColumns from "./hooks/useTableSimulacaoColumns";
import ModalComposicaoConta from "components/ModalComposicaoConta";
import HeaderTable from "components/Page/HeaderTable";
import { ArrecadacaoItemResponse } from "features/arrecadacoes/typings";
import { Flex, Text } from "@chakra-ui/react";
import SelectMonthYear from "ui/components/Input/SelectMonthYear";
import moment from "moment";
import { TipoArrecadacaoEnum } from "features/arrecadacoes/enums";
import { formatMoney } from "infra/helpers/number";
import { OperacaoEnum } from "core/resources/enums/conta";
import ButtonResumoPorCategoria from "components/Button/ButtonResumoPorCategoria";

interface ModalSimulacaoProps {
  isOpen: boolean;
  onClose: () => any;
  mes: string;
  ano: string;
  selectedArrecadacoes?: ArrecadacaoItemResponse[];
}

function ModalSimulacao({
  isOpen,
  onClose,
  mes,
  ano,
  selectedArrecadacoes,
}: ModalSimulacaoProps) {
  const [search, setSearch] = useState<string | undefined>();
  const [selectedItemComposicoes, setSelectedItemComposicoes] = useState<
    SimulacaoArrecadacaoItem | undefined
  >();
  const [isOpenModalResumoCategoria, setIsOpenModalResumoCategoria] =
    useState(false);

  const [mesConsumo, setMesConsumo] = useState<string | undefined>(
    moment(`${ano}-${mes}`, "YYYY-MM").subtract(1, "month").format("YYYY-MM")
  );
  const { data, isLoading, isFetching } = useSimulacaoArrecadacao({
    mes,
    ano,
    arrecadacoesIds: selectedArrecadacoes?.map((arrecadacao) => arrecadacao.id),
    mesConsumo,
  });

  const columns = useTableSimulacaoColumns();

  useLoading(isLoading || isFetching);

  const filteredItems = (data?.items || []).filter((item) => {
    if (!search) {
      return true;
    }

    const fieldsToSearch = [item.descricao, item.nomePagador, item.unidade];

    return fieldsToSearch.some((field) =>
      field.toLowerCase().includes(search.toLowerCase())
    );
  });

  const temConsumo = selectedArrecadacoes?.some(
    (arrecadacao) =>
      arrecadacao.tipoArrecadacao.id ===
      TipoArrecadacaoEnum.ConsumoAguaGasEnergia
  );

  return (
    <>
      <Modal
        centered
        visible={isOpen}
        footer={
          <FooterModal
            showRegisterAnother={false}
            onClose={onClose}
            buttonCancelarText="Fechar"
          />
        }
        title={`Simular arrecadação - ${mes}/${ano}`}
        destroyOnClose
        onCancel={onClose}
        closable={true}
        width="1200px"
        keyboard={false}
      >
        <NewTable
          columns={columns}
          dataSource={filteredItems}
          onRow={(record) => ({
            onClick: () => setSelectedItemComposicoes(record),
            style: {
              cursor: "pointer",
            },
          })}
          title={() => (
            <Flex alignItems="flex-end">
              <Flex w="350px">
                <HeaderTable value={search || ""} onSearch={setSearch} />
              </Flex>

              {temConsumo ? (
                <Flex flexDir="column" ml={2}>
                  <Text>Mês de consumo</Text>
                  <SelectMonthYear
                    bg="white"
                    value={
                      mesConsumo ? moment(mesConsumo, "YYYY-MM") : undefined
                    }
                    onChange={(date) => setMesConsumo(date?.format("YYYY-MM"))}
                    placeholder="Mês do consumo"
                  />
                </Flex>
              ) : null}

              {data ? (
                <Flex flexDir="column" ml="auto" gap={2}>
                  <Flex marginLeft="auto" gap={4}>
                    <ButtonResumoPorCategoria
                      onClick={() => setIsOpenModalResumoCategoria(true)}
                    />

                    {/* <ExportMenu
                    onExportPdf={() => setExportType("pdf")}
                    onExportarXlsx={() => setExportType("xlsx")}
                  /> */}
                  </Flex>
                  {Number.isFinite(data?.valorTotal) ? (
                    <Flex flexDir="column" ml="auto">
                      <Text fontWeight="bold" textAlign="right">
                        Valor total
                      </Text>
                      <Text fontWeight="medium" textAlign="right">
                        {formatMoney(data?.valorTotal)}
                      </Text>
                    </Flex>
                  ) : null}
                </Flex>
              ) : null}
            </Flex>
          )}
        />
      </Modal>

      {selectedItemComposicoes ? (
        <ModalComposicaoConta
          title={
            selectedItemComposicoes
              ? `Composição de ${selectedItemComposicoes?.descricao} | ${selectedItemComposicoes?.unidade}`
              : undefined
          }
          isOpen={!!selectedItemComposicoes}
          onClose={() => setSelectedItemComposicoes(undefined)}
          data={{
            valorCorrecaoMonetaria: 0,
            valorInicial: selectedItemComposicoes.valor,
            subtotal:
              selectedItemComposicoes?.composicoes?.reduce(
                (acc, next) => acc + next.valor,
                0
              ) ?? undefined,
            composicoes: selectedItemComposicoes?.composicoes?.map(
              (composicao) => ({
                categoriaFinanceiraId: composicao.categoriaFinanceiraId,
                valor: composicao.valor,
                categoriaFinanceira: {
                  id: composicao.categoriaFinanceiraId,
                  nome: composicao.nome,
                  breadcrumb: composicao.breadcrumb,
                  operacao: composicao.operacao,
                },
              })
            ),
            retencoes: selectedItemComposicoes?.retencoes?.map((retencao) => ({
              categoriaFinanceiraId: retencao.categoriaFinanceiraId,
              valor: retencao.valor,
              categoriaFinanceira: {
                id: retencao.categoriaFinanceiraId,
                nome: retencao.nome,
                breadcrumb: retencao.breadcrumb,
                operacao: retencao.operacao,
              },
            })),
            totalRetencao: selectedItemComposicoes?.retencoes?.reduce(
              (acc, next) => (acc += next.valor),
              0
            ),
            descontoValidoAte: selectedItemComposicoes.descontoDataAte,
            desconto: selectedItemComposicoes.descontoVariavel,
            total: selectedItemComposicoes.total,
          }}
        />
      ) : null}

      {data && isOpenModalResumoCategoria ? (
        <ModalComposicaoConta
          tableTitle="Categoria"
          data={{
            composicoes: data?.resumoPorCategoria?.map((item) => ({
              valor: item.valor ?? 0,
              categoriaFinanceiraId: item.id,
              categoriaFinanceira: {
                breadcrumb: item.breadcrumb,
                operacao: item.operacao ?? OperacaoEnum.Receita,
                nome: item.nome,
                id: item.id,
              },
            })),
            total: data?.valorTotal,
          }}
          onClose={() => setIsOpenModalResumoCategoria(false)}
          isOpen={isOpenModalResumoCategoria}
          title="Resumo por categoria"
        />
      ) : null}
    </>
  );
}

export default ModalSimulacao;
